import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Activity, Filter, Download } from 'lucide-react';
import { format } from 'date-fns';
import { useAuth } from '@/hooks/useAuth';

const actionColors = {
  INSERT: 'bg-green-100 text-green-800',
  UPDATE: 'bg-blue-100 text-blue-800',
  DELETE: 'bg-red-100 text-red-800'
};

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: 'all',
    resource_type: 'all',
    user: 'all'
  });
  const { profile, isSuperAdmin } = useAuth();

  useEffect(() => {
    loadAuditLogs();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [logs, filters]);

  const loadAuditLogs = async () => {
    setIsLoading(true);
    try {
      let query = supabase
        .from('audit_logs')
        .select(`
          *,
          user_profile:profiles(full_name, email)
        `)
        .order('created_at', { ascending: false })
        .limit(200);
      
      if (!isSuperAdmin) {
        query = query.eq('university_id', profile.university_id);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      
      setLogs(data || []);
    } catch (error) {
      console.error('Error loading audit logs:', error);
    }
    setIsLoading(false);
  };

  const applyFilters = () => {
    let filtered = [...logs];
    
    if (filters.action !== 'all') {
      filtered = filtered.filter(log => log.action === filters.action);
    }
    
    if (filters.resource_type !== 'all') {
      filtered = filtered.filter(log => log.resource_type === filters.resource_type);
    }
    
    if (filters.user !== 'all') {
      filtered = filtered.filter(log => log.user_id === filters.user);
    }
    
    setFilteredLogs(filtered);
  };

  const exportLogs = () => {
    const csvContent = [
      ['Timestamp', 'User', 'Action', 'Resource Type', 'Resource ID'].join(','),
      ...filteredLogs.map(log => [
        new Date(log.created_at).toISOString(),
        log.user_profile?.email || 'System',
        log.action,
        log.resource_type,
        log.resource_id || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit_logs_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const uniqueActions = [...new Set(logs.map(log => log.action))];
  const uniqueResourceTypes = [...new Set(logs.map(log => log.resource_type))];
  const uniqueUsers = [...new Set(logs.map(log => log.user_profile?.email).filter(Boolean))];

  if (isLoading) {
    return (
      <div className="p-6 space-y-8 bg-gray-200 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <Skeleton className="h-8 w-1/3 mb-8" />
          <Skeleton className="h-96 w-full rounded-3xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8 bg-gray-200 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-neumorphic mb-2">Audit Logs</h1>
            <p className="text-gray-600">System activity and security audit trail</p>
          </div>
          <Button 
            onClick={exportLogs}
            className="neumorphic rounded-xl px-4 py-2 flex items-center gap-2"
          >
            <Download className="w-5 h-5" />
            Export CSV
          </Button>
        </div>

        {/* Filters */}
        <Card className="neumorphic rounded-3xl mb-6">
          <CardContent className="p-6">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-indigo-600" />
                <span className="font-medium text-neumorphic">Filters:</span>
              </div>
              
              <div className="flex flex-wrap gap-4">
                <Select value={filters.action} onValueChange={(value) => setFilters({...filters, action: value})}>
                  <SelectTrigger className="neumorphic-inset rounded-xl border-0 bg-gray-200 w-32">
                    <SelectValue placeholder="Action" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Actions</SelectItem>
                    {uniqueActions.map(action => (
                      <SelectItem key={action} value={action}>{action}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={filters.resource_type} onValueChange={(value) => setFilters({...filters, resource_type: value})}>
                  <SelectTrigger className="neumorphic-inset rounded-xl border-0 bg-gray-200 w-40">
                    <SelectValue placeholder="Resource" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Resources</SelectItem>
                    {uniqueResourceTypes.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {isSuperAdmin && (
                  <Select value={filters.user} onValueChange={(value) => setFilters({...filters, user: value})}>
                    <SelectTrigger className="neumorphic-inset rounded-xl border-0 bg-gray-200 w-48">
                      <SelectValue placeholder="User" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Users</SelectItem>
                      {uniqueUsers.map(email => (
                        <SelectItem key={email} value={email}>{email}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Audit Logs Table */}
        <Card className="neumorphic rounded-3xl">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-neumorphic flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Activity Log ({filteredLogs.length} entries)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Resource</TableHead>
                  <TableHead>Resource ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLogs.map(log => (
                  <TableRow key={log.id}>
                    <TableCell className="font-mono text-sm">
                      {format(new Date(log.created_at), 'MMM d, yyyy HH:mm:ss')}
                    </TableCell>
                    <TableCell>
                      {log.user_profile?.full_name || log.user_profile?.email || 'System'}
                    </TableCell>
                    <TableCell>
                      <Badge className={`${actionColors[log.action] || 'bg-gray-100 text-gray-800'} border-0`}>
                        {log.action}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">{log.resource_type}</TableCell>
                    <TableCell className="font-mono text-xs text-gray-500">
                      {log.resource_id?.substring(0, 8)}...
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            {filteredLogs.length === 0 && (
              <div className="text-center py-16 text-gray-500">
                <Activity className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg">No audit logs found</p>
                <p className="text-sm">Try adjusting your filters</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}